// Demo.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { FaUser } from 'react-icons/fa';

/* ─────────────── HELPERS ─────────────── */
const extractId = (refOrString) =>
  typeof refOrString === 'string' ? refOrString : refOrString?.id || '';

const truncate = (txt, n) =>
  !txt ? '' : txt.length > n ? `${txt.slice(0, n)}…` : txt;

/* ─────────────── COURSE CARD ─────────────── */
function CourseCard({ course }) {
  return (
    <div className="bg-gray-800 hover:bg-gray-700 rounded-lg shadow-lg p-5 flex flex-col h-full">
      {course.visuals && (
        <span className="self-end mb-2 bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
          Interactive Visuals
        </span>
      )}

      <h3 className="text-xl font-semibold mb-2">{course.title}</h3>
      <p className="text-gray-400 flex-grow">
        {truncate(course.description, 100)}
      </p>

      {course.website && (
        <a
          href={course.website}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-block bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded"
        >
          Visit Course
        </a>
      )}

      <div className="mt-3 text-sm text-gray-400">
        {course.authorData ? (
          <span>
            <FaUser className="inline-block mr-1" />
            {course.authorData.name}
          </span>
        ) : (
          'Author info unavailable'
        )}
      </div>
    </div>
  );
}

/* ───────────── SEMESTER BLOCK ───────────── */
function SemesterBlock({ sem, map }) {
  return (
    <div>
      <h4 className="text-lg font-medium mb-2">
        Semester {sem.sem} {sem.credits ? `– ${sem.credits} cr` : ''}
      </h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Core */}
        {sem.core.map((r) => {
          const id = extractId(r);
          const c = map[id];
          return c ? <CourseCard key={id} course={c} /> : null;
        })}

        {/* Electives */}
        {sem.electives?.map((r) => {
          const id = extractId(r);
          const c = map[id];
          return c ? <CourseCard key={id} course={c} /> : null;
        })}
      </div>
    </div>
  );
}

/* ───────────── YEAR BLOCK ───────────── */
function YearBlock({ year, map, defaultOpen = false }) {
    return (
    <details
        open={defaultOpen}
        className="mb-4 border-l-2 border-indigo-500 pl-3"
    >
      <summary className="cursor-pointer font-semibold text-lg">
        Year {year.year}
      </summary>
      <div className="mt-3 space-y-6">
        {year.semesters.map((s) => (
          <SemesterBlock key={s.sem} sem={s} map={map} />
        ))}
      </div>
    </details>
  );
}

/* ───────────── MAIN COMPONENT ───────────── */
export default function Demo() {
  const [coursesMap, setCoursesMap] = useState({});
  const [specializations, setSpecializations] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [ungrouped, setUngrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        // 1. COURSES
        const csnap = await getDocs(collection(db, 'courses'));
        const allCourses = await Promise.all(
          csnap.docs.map(async (d) => {
            const c = { id: d.id, ...d.data() };
            if (c.authorRef) {
              try {
                const a = await getDoc(c.authorRef);
                c.authorData = a.exists() ? a.data() : null;
              } catch {
                c.authorData = null;
              }
            }
            return c;
          })
        );
        const cmap = Object.fromEntries(allCourses.map((c) => [c.id, c]));
        setCoursesMap(cmap);

        // 2. SPECIALISATIONS
        const ssnap = await getDocs(collection(db, 'specializations'));
        setSpecializations(
          ssnap.docs.map((d) => {
            const data = d.data();
            const courses = (data.courseRefs || [])
              .map((id) => cmap[id])
              .filter(Boolean);
            return { id: d.id, ...data, courses };
          })
        );

        // 3. PROGRAMMES
        const psnap = await getDocs(collection(db, 'academicPrograms'));
        const progs = psnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProgrammes(
          progs.map((p) => ({
            ...p,
            years: Array.isArray(p.years) ? p.years : [],
          }))
        );

        // 4. UNGROUPED
        const grouped = new Set(
          specializations.flatMap((sp) => sp.courses.map((c) => c.id))
        );
        setUngrouped(
          Object.values(cmap).filter((c) => !grouped.has(c.id))
        );
      } catch (e) {
        console.error(e);
        setError('Failed to load data.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loader" /> {/* implement your spinner */}
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-red-500 text-center py-10">{error}</p>
    );
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen py-10 px-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-2">MindEngage</h1>
        <p className="text-center mb-10">Open Courses</p>

        {/* 2-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Academic Programmes */}
          <section>
            <h2 className="text-3xl font-bold mb-4">Academic Programmes</h2>
            {programmes.map((prog) => (
              <div key={prog.id} className="mb-8">
                <h3 className="text-2xl font-semibold">{prog.title}</h3>
                <p className="text-gray-400 mb-4">{prog.description}</p>
                {prog.years.map((y, idx) => (
                    <YearBlock
                        key={y.year}
                        year={y}
                        map={coursesMap}
                        defaultOpen={idx === 0}
                    />
                ))}
              </div>
            ))}
          </section>

          {/* Specialisations */}
          <section>
            <h2 className="text-3xl font-bold mb-4">Specializations</h2>
            <div className="space-y-8">
              {specializations.map((sp) => (
                <div key={sp.id}>
                  <h3 className="text-2xl font-semibold">{sp.title}</h3>
                  <p className="text-gray-400 mb-3">{sp.description}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {sp.courses.map((c) => (
                      <CourseCard key={c.id} course={c} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Stand-alone Courses */}
        {ungrouped.length > 0 && (
          <section className="mt-12">
            <h2 className="text-3xl font-bold mb-4">Courses</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {ungrouped.map((c) => (
                <CourseCard key={c.id} course={c} />
              ))}
            </div>
          </section>
        )}

        <div className="text-center mt-12">
          <Link
            to="/"
            className="inline-block bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded"
          >
            Back Home
          </Link>
        </div>
      </div>
    </div>
  );
}
